/**
 * DataTable component using @tanstack/react-table with Mantine UI components.
 * Provides outlier management with optimistic updates, row selection, and bulk operations.
 */
import { useMemo, useEffect, Fragment, useCallback } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePaginationParams } from '@/hooks/usePaginationParams';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  Table,
  Checkbox,
  Badge,
  Group,
  Button,
  Stack,
  Text,
  Box,
  Pagination,
  ActionIcon,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconEdit } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';

import { useDataEditorStore } from '@/store/dataEditorStore';
import { api } from '@/api/dataEditor';
import type { OutlierResponse } from '@/types/sensor';
import type { DataPoint, ParameterOption, NoteUpdate } from '@/types/dataEditor';
import { NoteEditCell } from './NoteEditCell';
import { EditManualMeasurementModal } from './EditManualMeasurementModal';
import { TESTER_BRANDS } from '@/api/measurements';

interface DataTableProps {
  data: DataPoint[];
  isLoading: boolean;
  selectedParameter: ParameterOption | null;
  tankId: string;
  startDateString: string;
  endDateString: string;
}

export function DataTable({ data, isLoading, selectedParameter, tankId, startDateString, endDateString }: DataTableProps) {
  const queryClient = useQueryClient();
  const { addPendingChange, updatePendingChange } = useDataEditorStore();
  const isMobile = useIsMobile();

  // Local table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // URL-based pagination state (Task 107 - preserves across query invalidations)
  const { page: urlPage, setPage: setUrlPage } = usePaginationParams();

  const [pagination, setPagination] = useState({
    pageIndex: urlPage - 1, // Convert from 1-based to 0-based
    pageSize: 50,
  });

  // Sync URL page to table pagination state
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: urlPage - 1,
    }));
  }, [urlPage]);

  // Update URL when table pagination changes
  useEffect(() => {
    const newPage = pagination.pageIndex + 1; // Convert from 0-based to 1-based
    if (newPage !== urlPage) {
      setUrlPage(newPage);
    }
  }, [pagination.pageIndex, urlPage, setUrlPage]);

  // Row expansion state (for mobile view)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpanded = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  // Edit modal state (task-125)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDataPoint, setEditingDataPoint] = useState<DataPoint | null>(null);

  const openEditModal = (dataPoint: DataPoint) => {
    setEditingDataPoint(dataPoint);
    setEditModalOpen(true);
  };

  // Hide note columns on mobile viewport, show expand column on mobile
  // Only show Test Kit column when there are manual entries (task-108)
  const hasManualEntries = useMemo(
    () => data?.some(item => item.source === 'manual') ?? false,
    [data]
  );

  useEffect(() => {
    setColumnVisibility({
      note: !isMobile,
      tester_brand: !isMobile && hasManualEntries,  // task-108: Only show when manual entries exist
      expand: isMobile,
    });
  }, [isMobile, hasManualEntries]);

  // Clean up stale expanded row IDs when data changes (prevents memory leak)
  // Uses functional update to avoid race conditions if data changes during setState
  useEffect(() => {
    if (!data?.length) return;
    setExpandedRows((prev) => {
      // Compute inside functional update to use latest data at update time
      const currentRowIds = new Set(data.map((_, index) => String(index)));
      const cleaned = new Set<string>();
      prev.forEach((id) => {
        if (currentRowIds.has(id)) {
          cleaned.add(id);
        }
      });
      // Only update if something was removed
      return cleaned.size !== prev.size ? cleaned : prev;
    });
  }, [data]);

  // Optimistic update mutation for single outlier
  const updateOutlierMutation = useMutation({
    mutationFn: (update: any) => api.updateOutlier(tankId, update),
    onMutate: async (update) => {
      // Cancel in-flight queries for this specific parameter
      await queryClient.cancelQueries({
        queryKey: ['outliers', selectedParameter?.value, tankId, startDateString, endDateString]
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData([
        'outliers',
        selectedParameter?.value,
        tankId,
        startDateString,
        endDateString
      ]);

      // Optimistically update cache with CORRECT key
      queryClient.setQueryData<OutlierResponse>(
        ['outliers', selectedParameter?.value, tankId, startDateString, endDateString],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((item: any) => {
              // Match by measurement_id if available
              if (update.measurement_id !== undefined && item.measurement_id === update.measurement_id) {
                return { ...item, excluded: update.excluded };
              }
              // Fallback to legacy matching
              if (item.timestamp === update.timestamp && item.table_name === update.table_name) {
                return { ...item, excluded: update.excluded };
              }
              return item;
            }),
          };
        }
      );

      // Track pending change (use measurement_id if available, fallback to timestamp)
      const changeId = update.measurement_id !== undefined
        ? `m-${update.measurement_id}`
        : update.timestamp || 'unknown';
      addPendingChange({
        id: changeId,
        type: 'outlier',
        status: 'pending',
      });

      return { previousData, changeId };
    },
    onError: (err, _update, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ['outliers', selectedParameter?.value, tankId, startDateString, endDateString],
          context.previousData
        );
      }

      notifications.show({
        title: 'Update failed',
        message: err.message,
        color: 'red',
      });

      // Mark as failed
      if (context?.changeId) {
        updatePendingChange(context.changeId, 'failed');
      }
    },
    onSuccess: (_data, _update, context) => {
      // Mark as completed
      if (context?.changeId) {
        updatePendingChange(context.changeId, 'completed');
      }

      notifications.show({
        title: 'Success',
        message: 'Outlier status updated',
        color: 'green',
      });
    },
    onSettled: () => {
      // Refetch with CORRECT key
      queryClient.invalidateQueries({
        queryKey: ['outliers', selectedParameter?.value, tankId, startDateString, endDateString]
      });
      // Invalidate analysis and dashboard caches so they refetch
      // with updated exclusion status when user navigates to those pages
      queryClient.invalidateQueries({ queryKey: ['analysis'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // Note update mutation
  const updateNoteMutation = useMutation({
    mutationFn: (update: NoteUpdate) =>
      api.updateNote(tankId, update),
    onMutate: async (update) => {
      // Optimistically update cache with CORRECT key
      await queryClient.cancelQueries({
        queryKey: ['outliers', selectedParameter?.value, tankId, startDateString, endDateString]
      });

      const previousData = queryClient.getQueryData([
        'outliers',
        selectedParameter?.value,
        tankId,
        startDateString,
        endDateString
      ]);

      queryClient.setQueryData<OutlierResponse>(
        ['outliers', selectedParameter?.value, tankId, startDateString, endDateString],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((item: any) =>
              item.measurement_id === update.measurement_id
                ? { ...item, note: update.note }
                : item
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (err, _update, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ['outliers', selectedParameter?.value, tankId, startDateString, endDateString],
          context.previousData
        );
      }

      notifications.show({
        title: 'Failed to save note',
        message: err.message,
        color: 'red',
      });
    },
    onSuccess: () => {
      // Silently succeed (optimistic update already applied)
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['outliers', selectedParameter?.value, tankId, startDateString, endDateString]
      });
    },
  });

  // Stable callback refs for mutations (prevents column re-renders)
  const handleOutlierUpdate = useCallback(
    (measurementId: number | undefined, tableName: DataPoint['table_name'], timestamp: string, excluded: boolean) => {
      // Prefer measurement_id for unified measurements table
      if (measurementId !== undefined) {
        updateOutlierMutation.mutate({
          measurement_id: measurementId,
          excluded,
        });
      } else {
        // Fallback to legacy mode
        updateOutlierMutation.mutate({
          table_name: tableName,
          timestamp,
          excluded,
        });
      }
    },
    [updateOutlierMutation]
  );

  const handleNoteUpdate = useCallback(
    (measurementId: number, note: string) => {
      updateNoteMutation.mutate({
        measurement_id: measurementId,
        note,
      });
    },
    [updateNoteMutation]
  );

  // Column definitions - stable reference (no mutation deps)
  const columns = useMemo<ColumnDef<DataPoint>[]>(
    () => [
      // Column 1: Exclude checkbox (first column) - SORTABLE
      {
        id: 'excluded',
        accessorKey: 'excluded',
        header: ({ column }) => (
          <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => column.toggleSorting()}>
            <Text size="sm" fw={500}>Exclude</Text>
            {column.getIsSorted() === 'asc' && <span>↑</span>}
            {column.getIsSorted() === 'desc' && <span>↓</span>}
          </Group>
        ),
        cell: ({ row, table }) => {
          const handleUpdate = (table.options.meta as { handleOutlierUpdate?: typeof handleOutlierUpdate })?.handleOutlierUpdate;
          const isPending = (table.options.meta as { isOutlierPending?: boolean })?.isOutlierPending ?? false;
          return (
            <Checkbox
              checked={row.original.excluded}
              onClick={(e) => e.stopPropagation()} // Prevent row expansion on checkbox click
              onChange={() => {
                handleUpdate?.(
                  row.original.measurement_id,
                  row.original.table_name,
                  row.original.timestamp,
                  !row.original.excluded
                );
              }}
              disabled={isPending}
            />
          );
        },
        enableSorting: true,
        size: 80,
      },
      // Column 2: Date - SORTABLE
      {
        accessorKey: 'timestamp',
        header: ({ column }) => (
          <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => column.toggleSorting()}>
            <Text size="sm" fw={500}>Date</Text>
            {column.getIsSorted() === 'asc' && <span>↑</span>}
            {column.getIsSorted() === 'desc' && <span>↓</span>}
          </Group>
        ),
        cell: ({ getValue, table }) => {
          const isMobile = (table.options.meta as any)?.isMobile ?? false;
          const timestamp = getValue<string>();

          const formatted = new Date(timestamp).toLocaleString(undefined, {
            year: isMobile ? undefined : 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });

          return <Text size="sm">{formatted}</Text>;
        },
        enableSorting: true,
        size: 150,
      },
      // Column 3: Value (parameter-specific formatting) - SORTABLE
      {
        accessorKey: 'value',
        header: ({ column }) => (
          <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => column.toggleSorting()}>
            <Text size="sm" fw={500}>Value</Text>
            {column.getIsSorted() === 'asc' && <span>↑</span>}
            {column.getIsSorted() === 'desc' && <span>↓</span>}
          </Group>
        ),
        cell: ({ getValue, table }) => {
          const value = getValue<number>();
          // Get current parameter from table meta
          const parameter = (table.options.meta as any)?.parameter || '';

          let formatted = '';
          if (parameter === 'po4') {
            formatted = value.toFixed(3);
          } else if (['ca', 'mg'].includes(parameter)) {
            formatted = Math.round(value).toString();
          } else {
            formatted = value.toFixed(2);
          }

          return <Text size="sm">{formatted}</Text>;
        },
        enableSorting: true,
        size: 100,
      },
      // Column 4: Source
      {
        accessorKey: 'source',
        header: 'Source',
        cell: ({ getValue }) => (
          <Badge variant="light" size="sm">
            {getValue<string>()}
          </Badge>
        ),
        size: 120,
      },
      // Column 5: Test Kit (manual only) - task-108
      {
        accessorKey: 'tester_brand',
        header: 'Test Kit',
        cell: ({ row }) => {
          const tester = row.original.tester_brand;
          if (!tester || row.original.source !== 'manual') {
            return <Text size="sm" c="dimmed">—</Text>;
          }
          // Find display label for tester
          const label = TESTER_BRANDS.find(t => t.value === tester)?.label || tester;
          return (
            <Badge variant="outline" size="sm" color="grape">
              {label}
            </Badge>
          );
        },
        size: 100,
      },
      // Column 6: Note (inline editable)
      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row, table }) => {
          const handleUpdate = (table.options.meta as { handleNoteUpdate?: typeof handleNoteUpdate })?.handleNoteUpdate;
          const isPending = (table.options.meta as { isNotePending?: boolean })?.isNotePending ?? false;
          return (
            <NoteEditCell
              value={row.original.note || ''}
              onSave={(newNote) => {
                handleUpdate?.(
                  row.original.measurement_id,
                  newNote
                );
              }}
              disabled={isPending}
            />
          );
        },
        size: 400,
      },
      // Column 7: Edit button (manual rows only) - task-125
      {
        id: 'edit',
        header: '',
        cell: ({ row, table }) => {
          const openEdit = (table.options.meta as any)?.openEditModal;
          // Only show edit button for manual entries
          if (row.original.source !== 'manual') return null;

          return (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openEdit?.(row.original);
              }}
              title="Edit manual entry"
            >
              <IconEdit size={16} />
            </ActionIcon>
          );
        },
        size: 40,
      },
      // Column 8: Expand chevron (mobile only)
      {
        id: 'expand',
        header: '',
        cell: ({ row, table }) => {
          const isMobile = (table.options.meta as any)?.isMobile ?? false;
          const expandedRows = (table.options.meta as any)?.expandedRows ?? new Set();
          if (!isMobile) return null;

          const isExpanded = expandedRows.has(row.id);
          return (
            <Box data-testid="expand-chevron" style={{ cursor: 'pointer' }}>
              {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
            </Box>
          );
        },
        size: 40,
      },
    ],
    [] // No deps - all dynamic values passed via table meta
  );

  // Initialize table
  const table = useReactTable({
    data: data || [],
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination,
    },
    // Pass parameter, mobile state, and handlers to column cells via meta
    meta: {
      parameter: selectedParameter?.value || '',
      isMobile,
      expandedRows,
      toggleRowExpanded,
      handleOutlierUpdate,
      handleNoteUpdate,
      isOutlierPending: updateOutlierMutation.isPending,
      isNotePending: updateNoteMutation.isPending,
      openEditModal,  // task-125
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    // Prevent pagination reset when data changes (e.g., after toggling exclusion)
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Stack gap="md">
      {/* Row count */}
      <Group justify="flex-end">
        <Text size="sm" c="dimmed">
          {table.getFilteredRowModel().rows.length} rows
        </Text>
      </Group>

      {/* Table */}
      <Box style={{ overflowX: 'auto' }}>
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.Th
                    key={header.id}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              // Show skeleton rows while loading
              Array.from({ length: 5 }).map((_, i) => (
                <Table.Tr key={`skeleton-${i}`}>
                  {columns.map((_, colIndex) => (
                    <Table.Td key={`skeleton-${i}-${colIndex}`}>
                      <Text ta="center" c="dimmed" size="sm">
                        ⋯
                      </Text>
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Text ta="center" c="dimmed" py="xl">
                    No data available
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <Table.Tr
                    onClick={() => {
                      // Checkbox clicks are handled via stopPropagation in checkbox onChange
                      if (isMobile) {
                        toggleRowExpanded(row.id);
                      }
                    }}
                    style={{
                      backgroundColor: row.original.excluded ? 'var(--mantine-color-red-0)' : undefined,
                      opacity: row.original.excluded ? 0.6 : 1,
                      cursor: isMobile ? 'pointer' : undefined,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                  {/* Expanded row content (mobile only) */}
                  {isMobile && expandedRows.has(row.id) && (
                    <Table.Tr key={`${row.id}-expanded`}>
                      <Table.Td
                        colSpan={row.getVisibleCells().length}
                        style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '12px 16px' }}
                      >
                        <Stack gap="sm">
                          <div>
                            <Text size="xs" fw={500} c="dimmed">Note</Text>
                            <NoteEditCell
                              value={row.original.note || ''}
                              onSave={(newNote) => {
                                handleNoteUpdate(
                                  row.original.measurement_id,
                                  newNote
                                );
                              }}
                              disabled={updateNoteMutation.isPending}
                            />
                          </div>
                          {/* Show test kit for manual entries (task-108) */}
                          {row.original.source === 'manual' && row.original.tester_brand && (
                            <div>
                              <Text size="xs" fw={500} c="dimmed">Test Kit</Text>
                              <Badge variant="outline" size="sm" color="grape">
                                {TESTER_BRANDS.find(t => t.value === row.original.tester_brand)?.label || row.original.tester_brand}
                              </Badge>
                            </div>
                          )}
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Page {table.getState().pagination.pageIndex + 1} of{' '}
          {table.getPageCount()}
        </Text>

        <Pagination
          total={table.getPageCount()}
          value={table.getState().pagination.pageIndex + 1}
          onChange={(page) => table.setPageIndex(page - 1)}
        />

        <Group gap="xs">
          <Button
            size="xs"
            variant="default"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="xs"
            variant="default"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </Group>
      </Group>

      {/* Edit Manual Measurement Modal (task-125) */}
      <EditManualMeasurementModal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingDataPoint(null);
        }}
        dataPoint={editingDataPoint}
        tankId={tankId}
        parameterKey={selectedParameter?.value || ''}
      />
    </Stack>
  );
}
